import { useState } from 'react'
import type { CreatorPlatform, OutreachPreviewGroup } from '@/api/client'
import { LANGUAGES, OPT_OUT_LINES, fillTemplate } from '@/lib/creators'
import { Button, Field, FieldGroup, Input, Select, Textarea } from '@/ui'
import { Section } from '../campaigns/shared'
import { GroupFields } from './parts'
import type { StepProps } from './StepTarget'
import { defaultGroup, templateOf, type GroupDraft } from './wizard'

function Preview({ text }: { text: string }) {
  return (
    <div className="rounded-md border border-dashed border-border bg-muted/40 p-3">
      <p className="mb-1.5 text-meta text-muted-foreground">
        템플릿을 채운 모습이다. 사람마다 맞춤을 켜면 실제 초안은 이 틀을 바탕으로 사람마다 다르게 쓰이고, 만든 뒤 승인 대기에서 볼 수 있다.
      </p>
      <p className="whitespace-pre-wrap break-words text-table">{text}</p>
    </div>
  )
}

/** Step 3 — 메시지: the product once, then each group's approach.
 *  "미리 보기" fills the template on this page; the sample endpoint needs an
 *  outreach that exists, so real drafts are seen after step 4. */
export function StepMessage({
  s,
  set,
  groups,
  platform,
}: StepProps & { groups: OutreachPreviewGroup[]; platform?: CreatorPlatform }) {
  const [open, setOpen] = useState<Record<string, boolean>>({})
  const patchGroup = (key: string, patch: Partial<GroupDraft>) =>
    set({ groups: { ...s.groups, [key]: { ...(s.groups[key] ?? defaultGroup()), ...patch } } })
  const copyFirstToAll = () => {
    const first = groups[0]
    if (!first) return
    const src = s.groups[first.key] ?? defaultGroup()
    const next: Record<string, GroupDraft> = {}
    for (const g of groups) next[g.key] = { ...src }
    set({ groups: next })
  }
  return (
    <div className="space-y-5">
      <FieldGroup title="상품" description="모든 그룹이 같이 쓴다." columns={2}>
        <Field label="상품 이름" required>
          <Input value={s.productName} onChange={(e) => set({ productName: e.target.value })} />
        </Field>
        <Field label="상품 주소" required hint="링크를 누르면 이 주소로 간다. 추적 표시가 붙는다.">
          <Input value={s.productUrl} onChange={(e) => set({ productUrl: e.target.value })} placeholder="https://" inputMode="url" />
        </Field>
        <Field label="한 줄 설명" className="sm:col-span-2" hint="맞춤 메시지를 쓸 때 참고한다.">
          <Textarea rows={2} value={s.productBrief} onChange={(e) => set({ productBrief: e.target.value })} />
        </Field>
        <Field label="메시지 언어">
          <Select value={s.language} onChange={(e) => set({ language: e.target.value as typeof s.language })}>
            {LANGUAGES.map((l) => (
              <option key={l.value} value={l.value}>
                {l.label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="서명" hint="비워 두면 붙이지 않는다.">
          <Input value={s.signoff} onChange={(e) => set({ signoff: e.target.value })} />
        </Field>
      </FieldGroup>
      <p className="text-meta text-muted-foreground">
        마지막 줄에는 시스템이 거절 안내를 붙인다: “{OPT_OUT_LINES[s.language]}”
      </p>
      {groups.length > 1 && (
        <div>
          <Button size="sm" onClick={copyFirstToAll}>
            첫 그룹 설정을 모두에 적용
          </Button>
        </div>
      )}
      {groups.map((g) => {
        const d = s.groups[g.key] ?? defaultGroup()
        const template = templateOf(s, d)
        const who = g.sample[0]?.display_name || (g.sample[0] ? `@${g.sample[0].handle}` : '이름')
        return (
          <Section key={g.key} title={g.label} aside={`${g.count}명`} label={`${g.label} 그룹 메시지`}>
            <GroupFields
              idPrefix={g.label}
              language={s.language}
              platform={platform}
              value={{ offer: d.offer, angle: d.angle, tone: d.tone, template, personalize: d.personalize }}
              onChange={(patch) => patchGroup(g.key, patch)}
            />
            <div>
              <Button size="sm" onClick={() => setOpen({ ...open, [g.key]: !open[g.key] })}>
                {open[g.key] ? '미리 보기 닫기' : '미리 보기'}
              </Button>
            </div>
            {open[g.key] && <Preview text={fillTemplate(template, who, s.language)} />}
          </Section>
        )
      })}
    </div>
  )
}
